import { variantToOrderItem } from './orderItemCalc';
import type { FulfillmentMode, OrderItem, PosVariant } from '../types';
import type { VoucherItem } from '../components/CustomerSidebar/VoucherModal';

/**
 * Dòng hàng "quà tặng" từ voucher free_gift — giá luôn 0đ, số lượng theo
 * cấu hình voucher (KHÔNG theo mặc định `1` của `variantToOrderItem`).
 */
export function buildGiftOrderItem(
  variant: PosVariant,
  giftQuantity: number,
  voucherCode: string,
  fulfillmentMode: FulfillmentMode,
  newId?: () => string,
): OrderItem {
  const base = newId
    ? variantToOrderItem(variant, fulfillmentMode, newId)
    : variantToOrderItem(variant, fulfillmentMode);
  return {
    ...base,
    quantity: giftQuantity,
    price: 0,
    discountPercent: 0,
    discountAmount: 0,
    totalAmount: 0,
    retailPrice: base.price,
    is_gift: true,
    gift_promotion_id: voucherCode,
    gift_source: 'voucher',
  };
}

/**
 * Dòng quà là quà VOUCHER (nguồn `VoucherModal` resolve), tách khỏi quà
 * CHƯƠNG TRÌNH KHUYẾN MÃI (CTKM/BXGY, `gift_source === 'promotion'`).
 *
 * Dòng thiếu `gift_source` (nạp lại từ đơn ĐÃ LƯU trước khi field này tồn
 * tại, hoặc dòng gift voucher cũ) mặc định coi là quà VOUCHER — đúng hành vi
 * lịch sử trước khi tính năng gift-tự-thêm-CTKM tồn tại.
 *
 * Dùng ở MỌI nơi cần lọc/xoá quà theo nguồn — `onGiftItemsChange` (thay TOÀN
 * BỘ quà voucher bằng danh sách mới mỗi lần `VoucherModal` xác nhận) và
 * `removeItem` (chỉ gỡ voucher tương ứng khi thu ngân tự xoá MỘT dòng quà
 * voucher, không phải quà CTKM) PHẢI dùng hàm này thay vì tự kiểm tra
 * `is_gift` — thiếu nó, xác nhận lại `VoucherModal` (dù không đổi voucher
 * nào) sẽ xoá sạch quà CTKM đang có trong giỏ vì nó chỉ biết trả về danh
 * sách quà voucher.
 */
export function isVoucherGiftItem(item: OrderItem): boolean {
  return !!item.is_gift && item.gift_source !== 'promotion';
}

const OBJECT_ID_RE = /^[0-9a-f]{24}$/i;

/**
 * Suy luận `gift_source` từ HÌNH DẠNG `gift_promotion_id` — CHỈ còn dùng làm
 * DỰ PHÒNG cho ĐƠN CŨ.
 *
 * TCR-1074 (final review, Minor) — BE giờ LƯU `gift_source` thật xuống
 * `Order.items[].gift_source` (đóng dấu ở cả đường voucher lẫn đường CTKM) và
 * `getOrderDetail` trả nó ra; `mapRawOrderDetail` đã pass through. Nên
 * `Orders/edit.tsx` dùng field thật trước (`item.gift_source ??
 * inferGiftSource(...)`) và chỉ rơi vào hàm này với đơn tạo TRƯỚC khi field
 * đó tồn tại. Comment cũ ở đây khẳng định "BE KHÔNG lưu gift_source, đây là
 * field FE-only" — SAI kể từ nhánh này, đã sửa.
 *
 * `promotion_id` (quà CTKM) LUÔN là ObjectId hex 24 ký tự; mã voucher do
 * người dùng đặt (chữ hoa/số tự chọn) không khớp hình dạng đó — cùng ranh
 * giới đã dùng để chứng minh `pruneVouchersForRemovedGift` an toàn (xem test
 * "xoá quà CTKM ... không gỡ voucher nào của khách"). Thiếu bước suy luận
 * này, một quà CTKM đã lưu (từ phiên sửa đơn trước) sẽ hydrate với
 * `gift_source` rỗng → `isVoucherGiftItem` mặc định coi là quà voucher → lần
 * `VoucherModal` xác nhận tiếp theo (dù không đổi voucher nào) sẽ xoá nhầm nó.
 */
export function inferGiftSource(
  giftPromotionId: string | undefined,
): 'voucher' | 'promotion' | undefined {
  if (!giftPromotionId) return undefined;
  return OBJECT_ID_RE.test(giftPromotionId) ? 'promotion' : 'voucher';
}

/** Xoá các dòng gift ứng với voucher đã bị gỡ (bấm x ở khung khuyến mãi, hoặc revalidate lúc checkout drop voucher). */
export function pruneGiftItemsForCodes(items: OrderItem[], codes: string[]): OrderItem[] {
  if (codes.length === 0) return items;
  const codeSet = new Set(codes);
  return items.filter(
    (i) => !(isVoucherGiftItem(i) && i.gift_promotion_id && codeSet.has(i.gift_promotion_id)),
  );
}

/**
 * Dòng quà của CHƯƠNG TRÌNH khuyến mãi (BXGY), khác quà của voucher.
 *
 * Hình dạng dòng giống hệt `buildGiftOrderItem`, chỉ khác ý nghĩa của
 * `gift_promotion_id`: ở đây là `promotion_id` từ `applied_promotions`, không
 * phải mã voucher. Vì thế `pruneVouchersForRemovedGift` KHÔNG áp cho dòng này
 * — xoá quà CTKM không được gỡ voucher nào của khách.
 */
export function buildPromotionGiftOrderItem(
  variant: PosVariant,
  giftQuantity: number,
  promotionId: string,
  fulfillmentMode: FulfillmentMode,
  newId?: () => string,
): OrderItem {
  return {
    ...buildGiftOrderItem(variant, giftQuantity, promotionId, fulfillmentMode, newId),
    gift_source: 'promotion',
  };
}

/** Xoá voucher khỏi danh sách khi thu ngân tự xoá dòng gift tương ứng trong bảng sản phẩm. */
export function pruneVouchersForRemovedGift(
  vouchers: VoucherItem[],
  removedGiftPromotionId: string | undefined,
): VoucherItem[] {
  if (!removedGiftPromotionId) return vouchers;
  const next = vouchers.filter((v) => v.code !== removedGiftPromotionId);
  return next.length === vouchers.length ? vouchers : next;
}
